/**
 * Post-interview AI rubric assessment.
 *
 * After an AI interview session completes, this module takes the transcript
 * and a rubric template, runs a structured LLM evaluation, and persists
 * competency scores + a narrative summary on the session row.
 *
 * DB columns (migration 092):
 *   assessment_scores   jsonb   — { communication: 4, technical: 3, ... }
 *   assessment_summary  text    — LLM-generated narrative
 *   assessment_rubric_version text
 *   assessed_at         timestamptz
 */

import { supabase } from '@core/supabase/client';

export interface RubricDimension {
  key: string;
  label: string;
  weight: number;
  description: string;
}

export const DEFAULT_RUBRIC: RubricDimension[] = [
  { key: 'communication', label: 'Communication', weight: 0.2, description: 'Clarity, articulation, active listening' },
  { key: 'technical', label: 'Technical Knowledge', weight: 0.25, description: 'Domain expertise, depth of understanding' },
  { key: 'problem_solving', label: 'Problem Solving', weight: 0.2, description: 'Analytical thinking, structured approach' },
  { key: 'cultural_fit', label: 'Cultural Fit', weight: 0.15, description: 'Values alignment, team orientation' },
  { key: 'motivation', label: 'Motivation & Drive', weight: 0.1, description: 'Enthusiasm, career goals alignment' },
  { key: 'professionalism', label: 'Professionalism', weight: 0.1, description: 'Demeanor, preparedness, follow-through' },
];

export const RUBRIC_VERSION = '1.0';

export type AssessmentScores = Record<string, number>;

export interface AssessmentResult {
  scores: AssessmentScores;
  compositeScore: number;
  summary: string;
  rubricVersion: string;
}

function buildAssessmentPrompt(
  transcript: Array<{ role: string; content: string }>,
  rubric: RubricDimension[],
  jobTitle?: string,
): string {
  const transcriptText = transcript
    .map((m) => `${m.role === 'assistant' ? 'Interviewer' : 'Candidate'}: ${m.content}`)
    .join('\n');

  const rubricText = rubric
    .map((d) => `- ${d.label} (weight: ${d.weight}): ${d.description}`)
    .join('\n');

  return `You are an expert interview assessor. Evaluate the following interview transcript against the rubric below.

${jobTitle ? `Job Title: ${jobTitle}\n` : ''}
## Rubric
${rubricText}

## Transcript
${transcriptText}

## Instructions
For each rubric dimension, assign a score from 1 (poor) to 5 (excellent).
Then write a concise assessment summary (3-5 sentences) highlighting key strengths, areas for improvement, and overall recommendation.

Respond ONLY with valid JSON in this exact format:
{
  "scores": { ${rubric.map((d) => `"${d.key}": <1-5>`).join(', ')} },
  "summary": "<3-5 sentence assessment>"
}`;
}

function parseAssessmentResponse(
  raw: string,
  rubric: RubricDimension[],
): { scores: AssessmentScores; summary: string } {
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('No JSON found in assessment response');

  const parsed = JSON.parse(jsonMatch[0]);

  const scores: AssessmentScores = {};
  for (const dim of rubric) {
    const val = Number(parsed.scores?.[dim.key]);
    scores[dim.key] = Number.isFinite(val) ? Math.min(5, Math.max(1, Math.round(val))) : 3;
  }

  const summary =
    typeof parsed.summary === 'string' && parsed.summary.trim().length > 0
      ? parsed.summary.trim()
      : 'Assessment completed. See individual scores for details.';

  return { scores, summary };
}

export function computeCompositeScore(scores: AssessmentScores, rubric: RubricDimension[] = DEFAULT_RUBRIC): number {
  let weightedSum = 0;
  let totalWeight = 0;
  for (const dim of rubric) {
    const s = scores[dim.key] ?? 3;
    weightedSum += s * dim.weight;
    totalWeight += dim.weight;
  }
  return totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 20) : 60;
}

/**
 * Run assessment on a completed AI interview session.
 * Calls the org's LLM (Groq via ggj_b2b_llama edge function) to evaluate
 * the transcript, then persists results on the session row.
 */
export async function assessInterviewSession(
  sessionId: string,
  orgId: string,
  transcript: Array<{ role: string; content: string }>,
  options?: {
    rubric?: RubricDimension[];
    jobTitle?: string;
  },
): Promise<AssessmentResult> {
  const rubric = options?.rubric ?? DEFAULT_RUBRIC;
  const prompt = buildAssessmentPrompt(transcript, rubric, options?.jobTitle);

  const { data: fnData, error: fnErr } = await supabase.functions.invoke('ggj_b2b_llama', {
    body: {
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.3,
      max_tokens: 1024,
    },
  });

  if (fnErr) throw new Error(`Assessment LLM call failed: ${fnErr.message}`);

  const rawContent =
    fnData?.choices?.[0]?.message?.content ?? fnData?.content ?? '';
  if (!rawContent) throw new Error('Empty response from assessment LLM');

  const { scores, summary } = parseAssessmentResponse(rawContent, rubric);
  const compositeScore = computeCompositeScore(scores, rubric);

  const { error: updateErr } = await supabase
    .from('ggj_b2b_ai_interview_sessions')
    .update({
      assessment_scores: scores,
      assessment_summary: summary,
      assessment_rubric_version: RUBRIC_VERSION,
      assessed_at: new Date().toISOString(),
    })
    .eq('id', sessionId)
    .eq('org_id', orgId);

  if (updateErr) {
    console.error('[interviewAssessment] Failed to persist assessment:', updateErr);
  }

  return {
    scores,
    compositeScore,
    summary,
    rubricVersion: RUBRIC_VERSION,
  };
}
